/**
 * @copyright (C) 2024, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Heeseok Shin (hsshin@rsupport.com)
 *
 * @description
 *
 * @created at Thu Nov 21 2024
 **/

import { Ticker } from "@/frameworks/libraries";
import { Properties } from "@/frameworks/libraries/properties";

class ServiceHeartbeatStatic {
	#controllers: WeakMap<symbol, HeartbeatController>;
	#status: "init" | "idle" | "running";
	#keys: Map<string, symbol>;

	constructor() {
		this.#controllers = new WeakMap<symbol, HeartbeatController>();
		this.#keys = new Map<string, symbol>();
		this.#status = "init";
		this.addEvents();
	}

	public start(key: string, interval: number, strategy: "worker", callback?: () => void) {
		if (this.has(key)) {
			this.stop(key);
		}
		const keyObj = pollyfillWeakMapKey(key);
		this.#keys.set(key, keyObj);
		this.#controllers.set(keyObj, new HeartbeatController(new Ticker(interval, strategy)).start(callback));
		return this.#controllers;

		function pollyfillWeakMapKey(key: string) {
			try {
				const weakMap = new WeakMap<symbol, unknown>();
				//verify is available symbol
				weakMap.set(Symbol(key), null);
				return Symbol(key);
			} catch (error) {
				return Object(key);
			}
		}
	}

	public stop(key: string) {
		const keyObj = this.#keys.get(key);
		if (keyObj) {
			const controller = this.#controllers.get(keyObj);
			controller?.stop();
			this.#controllers.delete(keyObj);
			this.#keys.delete(key);
		}
	}

	public stopAll() {
		this.#keys.forEach((keyObj) => {
			const controller = this.#controllers.get(keyObj);
			controller?.stop();
			this.#controllers.delete(keyObj);
		});
		this.#keys.clear();
	}

	public get(key: string) {
		const keyObj = this.#keys.get(key);
		return keyObj ? this.#controllers.get(keyObj) : undefined;
	}

	public getAll() {
		return this.#keys;
	}

	public has(key: string) {
		const keyObj = this.#keys.get(key);
		return keyObj ? this.#controllers.has(keyObj) : false;
	}

	private addEvents() {
		Properties.on("ticker_changed", () => {
			let hasRunning = false;
			this.#keys.forEach((keyObj) => {
				const controller = this.#controllers.get(keyObj);
				if (controller?.isRunning()) {
					hasRunning = true;
				}
			});

			if (hasRunning) {
				console.log("#HeartbeatManager#", "running");
				this.setStatus("idle");
			} else {
				console.log("#HeartbeatManager#", "idle");
				this.setStatus("running");
			}
		});
	}

	private setStatus(status: "init" | "idle" | "running") {
		this.#status = status;
	}

	public get status() {
		return this.#status;
	}
}

const serviceHeartbeatStatic = new ServiceHeartbeatStatic();
export default serviceHeartbeatStatic;

class HeartbeatController {
	#ticker: Ticker;
	#isRunning: boolean;
	constructor(v: Ticker) {
		this.#isRunning = false;
		this.#ticker = this.createProxy(v);
	}

	public start(callback?: () => void) {
		console.log("start?", this.ticker);
		// eslint-disable-next-line @typescript-eslint/no-unused-vars
		this.ticker.start((elapseTime) => {
			// console.log("#HeartbeatController# elapsedTime", elapseTime, this.#isRunning);
			callback?.();
		});
		return this;
	}

	public stop() {
		this.ticker.stop();
		return this;
	}
	public isRunning() {
		return this.#isRunning;
	}

	private createProxy(v: Ticker): Ticker {
		return new Proxy(v, {
			set: (target, property, value) => {
				console.log(`#HeartbeatController# ${String(property)} changed to`, value);
				const _property = this.checkIsRunning(property, value);
				// @ts-ignore
				target[property as keyof Ticker] = value;
				if (_property) {
					setTimeout(() => {
						Properties.emit("ticker_changed");
					}, 0);
				}
				return true;
			}
		});
	}

	private checkIsRunning(property: string | symbol, value: unknown) {
		if (property === "timer" || property === "_worker") {
			this.#isRunning = Boolean(value);
			return property;
		}
	}

	private get ticker() {
		return this.#ticker;
	}
}
